import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'

export default class AosWrapper extends AlphaModule {
  constructor(...args) {
    super(...args)

    this._aos = null
    this._aosState = AlphaHelpers.getLibStates().stateNone
    this._aosResponsive = null
    this._aosResponsiveState = AlphaHelpers.getLibStates().stateNone
  }

  getName() {
    return 'aosWrapper'
  }

  getDefaultConfig() {
    const aosAttribute = 'data-aos'
    const aosResponsiveAttribute = `${aosAttribute}-responsive`

    return {
      aosAttribute,
      aosSelector: '[data-aos]',
      aosResponsiveAttribute,
      aosResponsiveSelector: `[${aosResponsiveAttribute}]`,
      aosDataAttrs: ['offset', 'delay', 'duration', 'easing', 'once', 'mirror', 'anchor-placement']
    }
  }

  _onLoad() {
    super._onLoad()

    this._getManager().addModuleEventListener(this.getName(), 'aos.loaded', () => {
      // some elements require refresh when they're added to page / loaded (like images)
      this._handleDomChanges()
    }, true)

    this.initAos()
  }

  _hasAnyAosElement() {
    return document.querySelector(this.getConfig().aosSelector) !== null
  }

  _hasAnyAosResponsiveElement() {
    return document.querySelector(this.getConfig().aosResponsiveSelector) !== null
  }

  _shouldLoadAos() {
    return AlphaHelpers.getLibStates().stateNone === this.getAosState() && this._hasAnyAosElement()
  }

  _shouldLoadAosResponsive() {
    return AlphaHelpers.getLibStates().stateNone === this.getAosResponsiveState() && this._hasAnyAosResponsiveElement()
  }

  async _loadAosAsync() {
    // mark AOS as loading to ensure we don't load it multiple times
    this._aosState = AlphaHelpers.getLibStates().stateLoading

    await import(/* webpackChunkName: "aos" */ '../../vendors/_aos')
      .then(({
        default: AOS
      }) => {
        this._aos = AOS
      })

    // mark AOS as loaded and inform other components
    this._aosState = AlphaHelpers.getLibStates().stateLoaded

    this.dispatchEvent('aos.loaded', {
      aos: this.getAos()
    })
  }

  async _loadAosResponsiveAsync() {
    // mark AOS responsive as loading to ensure we don't load it multiple times
    this._aosResponsiveState = AlphaHelpers.getLibStates().stateLoading

    await import(/* webpackChunkName: "aos-responsive" */ '../../components/aos-responsive')
      .then(({
        default: AosResponsive
      }) => {
        this._aosResponsive = new AosResponsive(this)
      })

    // mark AOS responsive as loaded and inform other components
    this._aosResponsiveState = AlphaHelpers.getLibStates().stateLoaded

    this.dispatchEvent('aos.responsive.loaded', {
      aosResponsive: this.getAosResponsive()
    })
  }

  async _maybeCallAosMethod(methodName) {
    if (this._shouldLoadAos()) {
      await this._loadAosAsync()
    }

    if (this._shouldLoadAosResponsive()) {
      await this._loadAosResponsiveAsync()
    }

    const aos = this.getAos()
    if (aos) {
      if (AlphaHelpers.objectHasOwnProperty(aos, methodName) && typeof aos[methodName] === 'function') {
        aos[methodName]()
      }
    }
  }

  _handleDomChanges() {
    if ('ResizeObserver' in window) {
      const mainEl = document.querySelector('.site-main')
      if (mainEl) {
        // just like other observers, ResizeObserver has an initial tick
        // don't trigger refresh hard on init
        let isInit = true
        const resizeObserver = new ResizeObserver(() => {
          if (!isInit) {
            this.refreshHardAos()
          }

          isInit = false
        })

        resizeObserver.observe(mainEl)
      }
    }
  }

  getAos() {
    return this._aos
  }

  getAosState(toString = false) {
    const aosState = this._aosState

    return toString ? AlphaHelpers.getLibStateString(aosState) : aosState
  }

  getAosResponsive() {
    return this._aosResponsive
  }

  getAosResponsiveState(toString = false) {
    const aosResponsiveState = this._aosResponsiveState

    return toString ? AlphaHelpers.getLibStateString(aosResponsiveState) : aosResponsiveState
  }

  /* WRAPPERS FOR AOS METHODS */

  initAos() {
    // potentially load AOS and call its init() method
    this._maybeCallAosMethod('init')
  }

  refreshAos() {
    // potentially load AOS and call its refresh() method
    this._maybeCallAosMethod('refresh')
  }

  refreshHardAos() {
    // potentially load AOS and call its refreshHard() method
    this._maybeCallAosMethod('refreshHard')
  }
}
